/**
 * Indicator Worker Bridge
 *
 * Provides a promise-based API for the main thread to communicate with
 * the indicator Web Worker. Handles request/response correlation, timeouts,
 * and graceful fallback to main-thread calculation if the worker fails.
 *
 * Performance benefits:
 *  - All indicator math runs off the UI thread → zero jank
 *  - Incremental mode: only recalculates a small window for live ticks → O(1)
 *  - Request deduplication: stale requests are automatically cancelled
 */

import { CandleData } from "../../types/chart.types";
import { PlotData } from "../types/indicator.types";
import { indicatorEngine } from "../engine/indicator-engine";

interface PendingRequest {
  resolve: (result: PlotData[]) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface WorkerResponse {
  requestId: number;
  result: PlotData[];
  error?: string;
}

export class IndicatorWorkerBridge {
  private worker: Worker | null = null;
  private requestId = 0;
  private pending: Map<number, PendingRequest> = new Map();
  private workerSupported = true;
  private readonly TIMEOUT_MS = 5000; // 5 second timeout per calculation

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      // Webpack 5 / Vite worker syntax — creates a separate bundle for the worker.
      // `type: "module"` is required by Vite so the browser loads it as ESM.
      this.worker = new Worker(
        new URL("./indicator.worker.ts", import.meta.url),
        { type: "module" }
      );
      this.worker.addEventListener("message", this.onMessage);
      this.worker.addEventListener("error", this.onError);
      console.log("[IndicatorWorkerBridge] Web Worker initialized successfully");
    } catch (err) {
      console.warn("[IndicatorWorkerBridge] Web Worker not supported, falling back to main thread:", err);
      this.workerSupported = false;
      this.worker = null;
    }
  }

  private onMessage = (event: MessageEvent<WorkerResponse>) => {
    const { requestId, result, error } = event.data;
    const pending = this.pending.get(requestId);
    if (!pending) return; // Stale response, already timed out or cancelled

    clearTimeout(pending.timer);
    this.pending.delete(requestId);

    if (error) {
      pending.reject(new Error(error));
    } else {
      pending.resolve(result);
    }
  };

  private onError = (event: ErrorEvent) => {
    console.error("[IndicatorWorkerBridge] Worker error:", event.message);
    // Reject all pending requests
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error("Worker crashed"));
    }
    this.pending.clear();

    // Try to reinitialize the worker
    this.dispose();
    this.initWorker();
  };

  /**
   * Calculate indicator data in the Web Worker (or main thread as fallback).
   *
   * @param indicatorId  The indicator ID (e.g., "sma", "rsi")
   * @param candles      Full candle data array
   * @param params       Indicator parameters
   * @param incremental  If true, uses the fast incremental path
   * @param cachedResult Previous full result for incremental merge
   */
  async calculate(
    indicatorId: string,
    candles: CandleData[],
    params: Record<string, any>,
    incremental = false,
    cachedResult?: PlotData[],
  ): Promise<PlotData[]> {
    // Fallback to main thread if worker is not available
    if (!this.workerSupported || !this.worker) {
      return this.calculateOnMainThread(indicatorId, candles, params);
    }

    const id = ++this.requestId;

    return new Promise<PlotData[]>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        // Timeout — fall back to main thread
        console.warn(`[IndicatorWorkerBridge] Timeout for ${indicatorId}, falling back to main thread`);
        resolve(this.calculateOnMainThread(indicatorId, candles, params));
      }, this.TIMEOUT_MS);

      this.pending.set(id, { resolve, reject, timer });

      this.worker!.postMessage({
        requestId: id,
        type: incremental ? "calculateIncremental" : "calculate",
        indicatorId,
        candles,
        params,
        cachedResult: incremental ? cachedResult : undefined,
      });
    }).catch(() => {
      // Worker doesn't know this indicator (e.g. newly added) — fallback
      return this.calculateOnMainThread(indicatorId, candles, params);
    });
  }

  /**
   * Synchronous fallback: calculate on the main thread using the existing engine.
   */
  private calculateOnMainThread(
    indicatorId: string,
    candles: CandleData[],
    params: Record<string, any>,
  ): PlotData[] {
    return indicatorEngine.calculate(indicatorId, candles, params);
  }

  /**
   * Cancel all pending requests (e.g., on symbol change or unmount).
   */
  cancelAll() {
    for (const [, pending] of this.pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error("Cancelled"));
    }
    this.pending.clear();
  }

  /**
   * Check if the Web Worker is active.
   */
  isWorkerActive(): boolean {
    return this.workerSupported && this.worker !== null;
  }

  /**
   * Clean up the worker.
   */
  dispose() {
    this.cancelAll();
    if (this.worker) {
      this.worker.removeEventListener("message", this.onMessage);
      this.worker.removeEventListener("error", this.onError);
      this.worker.terminate();
      this.worker = null;
    }
  }
}

/** Singleton instance shared across all chart panels */
export const indicatorWorkerBridge = new IndicatorWorkerBridge();
